import { ChangeEvent, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import Pagination from '@repo/ui/components/Pagination';
import { Search, RefreshCcw } from 'lucide-react';
import { SessionDetailDto } from '@repo/shared/dtos/analytics/SessionDataDto';
import { BaseTable } from '../shared/BaseTable';
import { TableColumn } from '../shared/types';
import { dateUtils } from '../utils/dateUtils';
import { createPagination } from '../utils/dataUtils';

interface SessionLogTableProps {
  items: SessionDetailDto[];
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  pageSize?: number;
  className?: string;
}

const DEFAULT_PAGE_SIZE = 12;
const SEARCH_PLACEHOLDER = 'Search by session or user';
const EMPTY_MESSAGE = 'No sessions match the current filters.';
const DATE_COLUMN_LABEL = 'Date';
const SESSION_COLUMN_LABEL = 'Session';
const USER_COLUMN_LABEL = 'Member';

const toLower = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.toLowerCase();
  }

  if (typeof value === 'number') {
    return String(value).toLowerCase();
  }

  return '';
};

const isValidName = (name: string | undefined): boolean => {
  return Boolean(name && name !== '' && name.toLowerCase() !== 'null');
};

const buildDisplayName = (item: SessionDetailDto): string => {
  const first = item.firstName?.trim();
  const last = item.lastName?.trim();

  if (isValidName(first) || isValidName(last)) {
    return [first, last].filter(isValidName).join(' ');
  }

  return `unnamed member`;
};

const buildColumns = (): TableColumn<SessionDetailDto>[] => [
  {
    key: 'date',
    label: DATE_COLUMN_LABEL,
    align: 'left',
    render: item => (
      <div className="font-medium text-[var(--anl-text)]">
        {dateUtils.formatDate(item.createdAt)}
      </div>
    ),
  },
  {
    key: 'session',
    label: SESSION_COLUMN_LABEL,
    render: item => (
      <div className="max-w-xs truncate" title={item.experienceTitle}>
        {item.experienceTitle}
      </div>
    ),
  },
  {
    key: 'user',
    label: USER_COLUMN_LABEL,
    render: item => (
      <div className="font-medium text-[var(--anl-text)]">{buildDisplayName(item)}</div>
    ),
  },
];

const createSearchPredicate = (query: string) => {
  if (!query) {
    return () => true;
  }

  return (item: SessionDetailDto) => {
    const sessionName = toLower(item.experienceTitle);
    const first = toLower(item.firstName);
    const last = toLower(item.lastName);
    const fullName = [first, last].filter(isValidName).join(' ');
    const displayName = toLower(buildDisplayName(item));
    const userId = toLower(item.userId);
    const sessionId = toLower(item.sessionId);

    return (
      sessionName.includes(query) ||
      (fullName && fullName.includes(query)) ||
      displayName.includes(query) ||
      userId.includes(query) ||
      sessionId.includes(query)
    );
  };
};

interface UseSessionLogTableStateOptions {
  items: SessionDetailDto[];
  pageSize: number;
}

const useSessionLogTableState = ({ items, pageSize }: UseSessionLogTableStateOptions) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const deferredSearch = useDeferredValue(searchTerm.trim().toLowerCase());

  useEffect(() => {
    setCurrentPage(1);
  }, [deferredSearch]);

  const filteredItems = useMemo(() => {
    if (!deferredSearch) {
      return items;
    }

    const predicate = createSearchPredicate(deferredSearch);
    return items.filter(predicate);
  }, [items, deferredSearch]);

  const pagination = useMemo(() => {
    return createPagination(filteredItems.length, currentPage, pageSize);
  }, [filteredItems.length, currentPage, pageSize]);

  useEffect(() => {
    if (!pagination.totalPages) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (currentPage > pagination.totalPages) {
      setCurrentPage(pagination.totalPages);
    }
  }, [currentPage, pagination.totalPages]);

  const pageItems = useMemo(() => {
    return filteredItems.slice(pagination.startIndex, pagination.endIndex);
  }, [filteredItems, pagination.endIndex, pagination.startIndex]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  return {
    searchTerm,
    handleSearchChange,
    currentPage,
    setCurrentPage,
    pageItems,
    pagination,
  };
};

export function SessionLogTable({
  items,
  isLoading,
  error,
  onRetry,
  pageSize = DEFAULT_PAGE_SIZE,
  className = '',
}: SessionLogTableProps) {
  const { searchTerm, handleSearchChange, currentPage, setCurrentPage, pageItems, pagination } =
    useSessionLogTableState({ items, pageSize });

  const columns = useMemo(buildColumns, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-[var(--anl-text-muted)]">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--anl-border)] border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-8 text-center">
        <div className="text-sm text-[var(--anl-text-muted)]">Failed to load session log.</div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-2 rounded-md border border-[var(--anl-border)] px-3 py-1.5 text-sm font-medium text-[var(--anl-text)] transition-colors hover:bg-[var(--anl-surface)]"
          >
            <RefreshCcw className="h-4 w-4" />
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`mb-1 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--anl-text-muted)]" />
          <input
            type="search"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={SEARCH_PLACEHOLDER}
            className="w-full rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface)] py-2 pl-9 pr-3 text-sm text-[var(--anl-text)] placeholder:text-[var(--anl-text-muted)] focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </div>
      </div>

      <BaseTable
        data={pageItems}
        columns={columns}
        emptyMessage={EMPTY_MESSAGE}
        keyExtractor={item => `${item.sessionId}-${item.createdAt.getTime()}`}
        className="overflow-hidden rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)]"
        rowClassName={() => 'hover:bg-[var(--anl-surface-elev)] transition-colors'}
      />

      {pagination.totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            totalPages={pagination.totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            variant="dark"
          />
        </div>
      )}
    </div>
  );
}
